import { doc, DocumentData, setDoc, Timestamp } from 'firebase/firestore'
import * as React from 'react'
import { Data } from 'react-firebase-hooks/firestore/dist/firestore/types'
import { firebaseAuth, firestore } from '../../../config/firebase'
import { Button } from '../../../shared-components/Button'
import { useScreenTracking } from '../../../shared-hooks/useScreenTracking'
import { USERS_COLLECTION } from '../../../shared-hooks/useUserData'
import { SESSIONS_SUBCOLLECTION } from '../../../shared-hooks/useUserSessions'
import { track } from '../../../utils/analytics'
import { SignupStep } from '../SignupFlow'
import { Day } from './Day'
import { Sessions } from './Sessions'

export enum TimeBlock {
  Morning,
  Afternoon,
  Evening,
  Overnight,
}

export const HOUR_MS = 60 * 60 * 1000
export const DAY_MS = 24 * HOUR_MS
export const TOMORROW_MS = new Date().setHours(0, 0, 0, 0) + DAY_MS

export const NUM_DAYS_TO_SHOW = 7

const formatDate = (date: Date): string => {
  const dateString = date.toLocaleString('default', {
    month: 'short',
    day: 'numeric',
  })
  const month = dateString.slice(0, 3)
  const day = dateString.slice(4)
  return `${month}. ${day}`
}

/**
 * Transform the day and time block into the firestore format (i.e. monday = 0
 * and overnight = 0). This gets complicated because we store the overnight
 * time block in the prior day, while firestore does not.
 *
 * We return the corresponding date, weekday, and starting hour of the passed
 * in session.
 */
const convertSessionToFirestoreFormat = (
  day: number,
  timeBlock: TimeBlock
): [Date, number, number] => {
  let date
  if (timeBlock === TimeBlock.Overnight) {
    date = new Date(TOMORROW_MS + (day + 1) * DAY_MS)
  } else {
    date = new Date(TOMORROW_MS + day * DAY_MS)
  }

  const weekday = (date.getDay() + 6) % 7
  const startTime = ((timeBlock + 1) % 4) * 6
  return [date, weekday, startTime]
}

/**
 * Rows indicate days, with the 0th index being the current day.
 * Each day contains an array of 4 ints, each representing the hours the user
 * wants to drive in that time block.
 * The first time block is from 6AM-12PM and the last is from 12AM-6AM (of the
 * next day).
 */
type SessionState = number[][]

const initialSessionState = (): SessionState => {
  const sessionState: SessionState = []
  for (let i = 0; i < NUM_DAYS_TO_SHOW; i++) {
    sessionState.push([0, 0, 0, 0])
  }
  return sessionState
}

type SessionPickerProps = {
  userData: Data<DocumentData, '', ''>
  setTotalGuarantee: React.Dispatch<React.SetStateAction<number>>
  setSignupStep: (step: SignupStep) => void
}

export const SessionPicker = ({
  userData,
  setTotalGuarantee,
  setSignupStep,
}: SessionPickerProps): JSX.Element => {
  useScreenTracking('SessionPickerView', 'View session picker screen')

  const [sessionState, setSessionState] = React.useState<SessionState>(
    initialSessionState()
  )
  const [activeDay, setActiveDay] = React.useState(0)
  const [isSaving, setIsSaving] = React.useState(false)
  const [guarantee, setGuarantee] = React.useState(0)
  const hasAddedSessions = React.useRef(false)

  const startDate = new Date(TOMORROW_MS)
  const endDate = new Date(TOMORROW_MS + (NUM_DAYS_TO_SHOW - 1) * DAY_MS)

  // Automatically scroll to the bottom on load to make it clear that the button
  // exists.
  React.useEffect(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      left: 0,
      behavior: 'smooth',
    })
  }, [])

  React.useEffect(() => {
    if (!userData) {
      return
    }
    const {
      rates: {
        base_rate: { rate },
        day_multipliers,
        time_block_multipliers,
      },
    } = userData

    let newGuarantee = 0
    sessionState.forEach((dayData, day) => {
      dayData.forEach((hours, timeBlock) => {
        const [, weekday, trueTimeBlock] = convertSessionToFirestoreFormat(
          day,
          timeBlock
        )
        const dayMultiplier = day_multipliers[weekday]
        const timeBlockMultiplier = time_block_multipliers[trueTimeBlock]

        const currGuarantee =
          (hours * rate * dayMultiplier * timeBlockMultiplier) / 100
        newGuarantee += currGuarantee
      })
    })
    setGuarantee(newGuarantee)
  }, [userData, sessionState])

  const handleSliderChange = (timeBlock: TimeBlock, hours: number) => {
    if (!hasAddedSessions.current) {
      track('AddSessions', 'Add sessions')
      hasAddedSessions.current = true
    }

    // We copy everything using the spread operator so we don't directly
    // manipulate any existing state.
    setSessionState((prevSessionState) => {
      const newSessionState = [...prevSessionState]
      const currDaySessions = [...newSessionState[activeDay]]

      // Short circuit if the state hasn't actually changed. This way we return
      // the same object and don't lose referential equality (which affects how
      // often the guarantee gets calculated).
      if (currDaySessions[timeBlock] === hours) {
        return prevSessionState
      }
      currDaySessions[timeBlock] = hours
      newSessionState[activeDay] = currDaySessions
      return newSessionState
    })
  }

  const handleClick = async () => {
    setIsSaving(true)

    track('Purchase', 'Save sessions', 'track')

    const userDocRef = doc(
      firestore,
      USERS_COLLECTION,
      firebaseAuth.currentUser.uid
    )
    const userUpdate = setDoc(
      userDocRef,
      {
        trial_end_date: Timestamp.fromDate(endDate),
        is_always_on: false,
      },
      { merge: true }
    )

    const firestoreWrites: Promise<void>[] = [userUpdate]
    sessionState.forEach((dayData, day) => {
      dayData.forEach((hours, timeBlock) => {
        if (!hours) {
          return
        }

        const [date, weekday, startHour] = convertSessionToFirestoreFormat(
          day,
          timeBlock
        )
        const startTime = new Date(date.getTime() + startHour * HOUR_MS)
        const endTime = new Date(startTime.getTime() + 6 * HOUR_MS)

        const sessionData = {
          start_time: Timestamp.fromDate(startTime),
          end_time: Timestamp.fromDate(endTime),
          duration_hours: hours,
          time_of_day: (timeBlock + 1) % 4,
          day_of_week: weekday,
          shift_online_time: 0,
          paid: 0,
          points: 0,
          earned: 0,
          timestamp: Timestamp.fromMillis(Date.now()),
        }

        const sessionDocRef = doc(
          firestore,
          USERS_COLLECTION,
          firebaseAuth.currentUser.uid,
          SESSIONS_SUBCOLLECTION,
          startTime.getTime().toString()
        )
        firestoreWrites.push(
          setDoc(sessionDocRef, sessionData, { merge: true })
        )
      })
    })

    await Promise.allSettled(firestoreWrites)

    setIsSaving(false)
    setTotalGuarantee(guarantee)
    setSignupStep(SignupStep.Plaid)
  }

  return (
    <article className='bg-white rounded-xl col-span-12 xl:col-span-7 px-8 md:px-6 xl:px-8 py-4 md:pt-16 md:pb-10 h-full md:h-auto md:m-auto flex flex-col gap-4 items-center justify-center'>
      <p className='text-sm font-light text-[#474953]'>
        {formatDate(startDate)} - {formatDate(endDate)} Guarantee
      </p>
      <p className='text-5xl font-semibold text-brand-light-purple'>
        ${guarantee.toFixed(2)}
      </p>
      <div className='flex gap-2 md:gap-4 justify-center my-2 md:my-8'>
        {[...Array(NUM_DAYS_TO_SHOW)].map((_, day) => (
          <Day
            key={day}
            isActive={day === activeDay}
            hasSessions={sessionState[day].reduce((acc, x) => acc + x) > 0}
            onClick={() => setActiveDay(day)}
            date={new Date(TOMORROW_MS + day * DAY_MS)}
            disabled={isSaving}
          />
        ))}
      </div>
      <Sessions
        onSliderChange={handleSliderChange}
        sessionHours={sessionState[activeDay]}
        disabled={isSaving}
      />
      <div className='w-full flex justify-end mt-3 mx-10 pr-4'>
        <Button
          width='auto'
          type='button'
          onClick={handleClick}
          isSubmitting={isSaving}
        >
          Start Free Trial
        </Button>
      </div>
    </article>
  )
}
